import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../auth/context/AuthContext';
import VoucherAdjustForm from '../../vouchers/components/VoucherAdjustForm';
import VoucherRedeemModal from '../../vouchers/components/VoucherRedeemModal';
import VoucherShareCard from '../../vouchers/components/VoucherShareCard';
import { useVoucherDetail } from '../../vouchers/hooks/useVoucherDetail';
import { useVoucherShare } from '../../vouchers/hooks/useVoucherShare';
import { formatDate, formatDateTime } from '../../vouchers/utils/voucherDates';
import { getMovementTypeLabel } from '../../vouchers/utils/voucherMovements';
import EditClientModal from '../../clients/components/EditClientModal';
import { ROUTES } from '../../../shared/config/routes';

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const section = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function AdminVoucherDetailPage() {
  const { voucherId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const voucherPreview = location.state?.voucherPreview || null;
  const { voucher, movements, movementsMeta, loading, busy, error, redeem, adjust, remove, reload } = useVoucherDetail(voucherId, user, voucherPreview);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const {
    cardRef,
    copyState,
    capturing,
    copyText,
    captureCardAsImage,
    buildWhatsAppHref,
    buildMailtoHref,
  } = useVoucherShare();

  if (loading && !voucher) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-12 shadow-[0_24px_60px_rgba(15,23,42,0.06)] flex flex-col items-center gap-4 text-slate-500"
      >
        <span className="w-8 h-8 border-3 border-slate-200 border-t-[var(--app-accent)] rounded-full animate-spin" />
        <p className="text-lg font-medium">Cargando bono...</p>
      </motion.div>
    );
  }

  if ((error && !voucher) || !voucher) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-[30px] border border-red-200 bg-red-50 p-8 text-red-600 shadow-[0_24px_60px_rgba(15,23,42,0.06)] space-y-4"
      >
        <p className="font-semibold">{error || 'Bono no encontrado'}</p>
        <Link
          to={ROUTES.adminVoucherList}
          className="inline-flex items-center justify-center rounded-[18px] bg-white px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
        >
          Volver a gestión de bonos
        </Link>
      </motion.div>
    );
  }

  const whatsappHref = buildWhatsAppHref(voucher);
  const mailtoHref = buildMailtoHref(voucher);

  async function handleDeleteVoucher() {
    if (!voucher || !hasRole('ADMIN')) {
      return;
    }

    const confirmed = window.confirm(`¿Seguro que quieres eliminar el bono ${voucher.voucherCode}? Esta acción no se puede deshacer.`);
    if (!confirmed) {
      return;
    }

    const deleted = await remove();
    if (deleted) {
      navigate(ROUTES.adminVoucherList);
    }
  }

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-amber-700 shadow-[0_12px_30px_rgba(15,23,42,0.05)]"
        >
          <p className="font-medium">{error}</p>
          <p className="mt-1 text-sm text-amber-600">Mostrando la última información disponible de este bono.</p>
        </motion.div>
      )}

      {/* Header */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-4 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <Link to={ROUTES.adminVoucherList} className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--app-accent)] hover:underline transition">
          ← Volver a bonos
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Detalle de bono</p>
            <h2 className="text-5xl leading-none font-semibold tracking-[-0.05em] text-slate-950 mt-2">{voucher.voucherCode}</h2>
            <div className="flex items-center gap-3 mt-3">
              <p className="text-slate-500">{voucher.customerName} · {voucher.phone}</p>
              <button
                type="button"
                onClick={() => setEditModalOpen(true)}
                className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200 hover:text-blue-600 transition flex items-center justify-center w-8 h-8"
                title="Editar datos del cliente"
              >
                <span className="text-sm">✎</span>
              </button>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3 lg:items-end">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 20 }}
              className="rounded-[24px] border border-[rgba(0,113,227,0.12)] bg-[rgba(0,113,227,0.08)] px-6 py-4 text-center min-w-[220px]"
            >
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--app-accent)]">Saldo disponible</p>
              <p className="text-4xl font-semibold tracking-[-0.04em] text-slate-950">{voucher.hoursAvailable}h</p>
            </motion.div>
            {hasRole('ADMIN') && (
              <button
                type="button"
                onClick={handleDeleteVoucher}
                disabled={busy}
                className="rounded-[18px] border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-100 disabled:opacity-60"
              >
                {busy ? 'Eliminando...' : 'Eliminar bono'}
              </button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          {[
            { label: 'Comprado', value: `${voucher.hoursPurchased}h`, size: 'text-2xl' },
            { label: 'Usado', value: `${voucher.hoursUsed}h`, size: 'text-2xl' },
            { label: 'Compra', value: formatDateTime(voucher.purchaseDate), size: 'text-lg' },
            { label: 'Vence', value: formatDate(voucher.expiresAt), size: 'text-lg' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.07, duration: 0.4 }}
              className="rounded-[22px] bg-[rgba(245,245,247,0.86)] p-4"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{stat.label}</p>
              <p className={`${stat.size} font-medium text-slate-950`}>{stat.value}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Visual voucher card + Share actions */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Tarjeta del bono</p>
          <h3 className="mt-2 text-2xl font-medium text-slate-950">Vista previa y reenvío</h3>
        </div>
        <VoucherShareCard voucher={voucher} cardRef={cardRef} />
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => copyText(voucher)}
            className="rounded-[20px] bg-[#0a84ff] hover:bg-[#0077ed] text-white px-5 py-3 font-medium transition"
          >
            {copyState === 'copied' ? '✓ Copiado' : 'Copiar resumen'}
          </button>
          <button
            type="button"
            onClick={() => captureCardAsImage(voucher)}
            disabled={capturing}
            className="rounded-[20px] bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 font-medium transition disabled:opacity-60"
          >
            {capturing ? 'Generando...' : '📸 Descargar imagen'}
          </button>
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="rounded-[20px] bg-[#25D366] hover:bg-[#20ba58] text-white px-5 py-3 font-medium transition"
            >
              Enviar por WhatsApp
            </a>
          )}
          <a
            href={mailtoHref || '#'}
            className={`rounded-[20px] px-5 py-3 font-medium transition ${mailtoHref ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 text-slate-400 pointer-events-none'}`}
          >
            Enviar por correo
          </a>
        </div>
      </motion.div>

      {/* Quick redeem actions */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-4 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <h3 className="text-2xl font-medium text-slate-950">Acciones rápidas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3].map((hours) => (
            <button
              key={hours}
              type="button"
              onClick={() => redeem(hours)}
              disabled={busy || voucher.hoursAvailable < hours}
              className="min-h-[72px] rounded-[22px] bg-[var(--app-accent)] hover:bg-[#0062c4] text-white text-2xl font-semibold disabled:bg-slate-300 transition active:scale-95"
            >
              {hours}h
            </button>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={() => setRedeemModalOpen(true)}
            className="min-h-[72px] rounded-[22px] bg-slate-100 hover:bg-slate-200 text-slate-950 text-lg font-medium disabled:bg-slate-100 disabled:text-slate-400 transition active:scale-95"
          >
            Canjear
          </button>
        </div>

        {hasRole('ADMIN') && (
          <VoucherAdjustForm voucher={voucher} onAdjust={adjust} busy={busy} />
        )}
      </motion.div>

      {/* History */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-4 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h3 className="text-2xl font-medium text-slate-950">Historial interno</h3>
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-500">Orden</label>
            <select
              value={movementsMeta.direction}
              onChange={(event) => {
                reload({
                  ...movementsMeta,
                  page: 0,
                  direction: event.target.value,
                });
              }}
              className="app-input-compact rounded-2xl"
            >
              <option value="desc">Más recientes</option>
              <option value="asc">Más antiguos</option>
            </select>
          </div>
        </div>
        <div className="space-y-3">
          {movements.length === 0 ? (
            <p className="text-slate-500">No hay movimientos registrados.</p>
          ) : (
            movements.map((movement, i) => (
              <motion.div
                key={movement.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
                className="border border-[var(--app-border)] rounded-[22px] p-4 bg-white/80 hover:shadow-md transition"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-950">{getMovementTypeLabel(movement.type)} · {movement.hours}h</p>
                    <p className="text-sm text-slate-500">{formatDateTime(movement.performedAt)}</p>
                  </div>
                  <div className="text-sm text-slate-600 text-right">
                    <p>{movement.performedByName || 'Sistema'}</p>
                  </div>
                </div>
                {movement.note && <p className="mt-3 text-sm text-slate-600">{movement.note}</p>}
              </motion.div>
            ))
          )}
        </div>
        {movementsMeta.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-[var(--app-border)]">
            <button
              type="button"
              disabled={movementsMeta.page === 0}
              onClick={() => reload({ ...movementsMeta, page: Math.max(movementsMeta.page - 1, 0) })}
              className="rounded-2xl bg-[rgba(245,245,247,0.9)] px-4 py-2 font-medium disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-sm text-slate-500">
              Página {movementsMeta.page + 1} de {movementsMeta.totalPages}
            </span>
            <button
              type="button"
              disabled={movementsMeta.page + 1 >= movementsMeta.totalPages}
              onClick={() => reload({ ...movementsMeta, page: movementsMeta.page + 1 })}
              className="rounded-2xl bg-[rgba(245,245,247,0.9)] px-4 py-2 font-medium disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </motion.div>

      {redeemModalOpen && (
        <VoucherRedeemModal
          voucher={voucher}
          busy={busy}
          onClose={() => setRedeemModalOpen(false)}
          onConfirm={async (hours) => {
            const success = await redeem(hours);
            if (success) {
              setRedeemModalOpen(false);
            }
            return success;
          }}
        />
      )}

      {editModalOpen && (
        <EditClientModal
          customer={{
            id: voucher.customerId,
            name: voucher.name,
            surname: voucher.surname,
            email: voucher.email,
            phone: voucher.phone,
            fullName: voucher.customerName
          }}
          actor={user}
          onClose={() => setEditModalOpen(false)}
          onSave={() => {
            setEditModalOpen(false);
            reload(movementsMeta);
          }}
        />
      )}
    </motion.div>
  );
}
